import { UserManager, WebStorageStateStore } from 'oidc-client-ts';

import { decodeBase64Url } from '@/utils/base64url';

export interface DebuggerOptions {
  clientId: string;
  loginHint: string;
  overrideCallbackUrl: string;
  acr: string;
  idp: string;
  responseType: string;
  scope: string;
  amr?: string;
  language?: string;
  loginContext?: string;
}

const OPTIONS_STORAGE_KEY = 'options';

export function getDefaultOptions(): DebuggerOptions {
  return {
    clientId: 'demo-client',
    loginHint: '99966634',
    overrideCallbackUrl: '',
    acr: '',
    idp: '',
    responseType: 'code',
    scope: 'openid profile ial2',
  };
}

export function loadOptions(): DebuggerOptions {
  const stored = localStorage.getItem(OPTIONS_STORAGE_KEY);

  if (!stored) return getDefaultOptions();

  try {
    return { ...getDefaultOptions(), ...(JSON.parse(stored) as Partial<DebuggerOptions>) };
  } catch {
    return getDefaultOptions();
  }
}

export function saveOptions(options: DebuggerOptions): void {
  localStorage.setItem(OPTIONS_STORAGE_KEY, JSON.stringify(options));
}

export function getCallbackUrl(environmentValue: string): string {
  return `${environmentValue}/ui/debugger/callback`;
}

export function getPostLogoutUrl(environmentValue: string): string {
  return `${environmentValue}/ui/debugger?logout=ok`;
}

/**
 * oidc-client-ts requires `authority` and `client_id` (they were optional in
 * oidc-client v1). The old DebuggerOutput built a UserManager without them,
 * which no longer works - so both debugger views now share this factory.
 */
export function createUserManager(environmentValue: string, options: DebuggerOptions): UserManager {
  return new UserManager({
    authority: environmentValue,
    client_id: options.clientId,
    redirect_uri: options.overrideCallbackUrl || getCallbackUrl(environmentValue),
    response_type: 'code',
    response_mode: 'query',
    scope: options.scope,
    loadUserInfo: true,
    post_logout_redirect_uri: getPostLogoutUrl(environmentValue),
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });
}

/** Decodes a JWT payload. Equivalent to the hand-rolled atob/percent-decode. */
export function parseJwt(jwt: string): Record<string, unknown> {
  return JSON.parse(decodeBase64Url(jwt.split('.')[1])) as Record<string, unknown>;
}
